// Wishlist interactions only. Standard saves originate in Swish; no purchase pipeline.
export const EVENT_NAMES = new Set([
  'wishlist_open', 'wishlist_item_click', 'wishlist_login_click', 'wishlist_remove',
  'wishlist_clear', 'wishlist_tab_view', 'saved_for_you_view', 'saved_for_you_created',
  'saved_for_you_promote', 'saved_for_you_dismiss', 'saved_for_you_clear', 'saved_for_you_toggle',
]);
const SOURCES = new Set(['header', 'save_success', 'manual', 'saved_for_you']);
const ACTIONS = new Set(['remove', 'undo', 'clear', 'on', 'off']);
const OUTCOMES = new Set(['added', 'already_saved']);
const numericId = value => /^\d{1,20}$/.test(String(value || '')) ? String(value) : '';

export function cleanEvent(name, data = {}) {
  if (!EVENT_NAMES.has(name) || !SOURCES.has(data.source)) return null;
  const params = { wishlist_source: data.source, wishlist_surface: data.surface === 'page' ? 'page' : 'popup' };
  const product = numericId(data.productId), variant = numericId(data.variantId);
  if (name === 'saved_for_you_created' && (data.source !== 'saved_for_you' || !product || !variant)) return null;
  if (product) params.product_id = product;
  if (variant) params.variant_id = variant;
  if (name === 'saved_for_you_created') return { name, params };
  if (Number.isInteger(data.count) && data.count >= 0 && data.count <= 10000) params.item_count = data.count;
  if (ACTIONS.has(data.action)) params.wishlist_action = data.action;
  if (OUTCOMES.has(data.outcome)) params.wishlist_outcome = data.outcome;
  return { name, params };
}

function allowed(privacy, method) {
  try { return privacy?.[method]?.() === true; } catch { return false; }
}

export function processingAllowed(privacy, analytics) {
  const consent = analytics?.current_consent;
  return {
    ga4: allowed(privacy, 'analyticsProcessingAllowed') && consent?.analytics_storage === 'granted',
    meta: allowed(privacy, 'marketingAllowed') && allowed(privacy, 'saleOfDataAllowed') &&
      consent?.ad_storage === 'granted' && consent?.ad_user_data === 'granted',
  };
}

export function createTracker({ getPrivacy, getAnalytics, getFlags, isProduction, google, meta, diagnose = () => {} }) {
  return (name, data) => {
    const event = cleanEvent(name, data);
    if (!event) return { ga4: 'invalid', meta: 'invalid' };
    const privacy = getPrivacy(), analytics = getAnalytics(), flags = getFlags();
    // Advertising is a separate purpose, including the visitor's data-sharing choice.
    const { ga4: gaAllowed, meta: metaAllowed } = processingAllowed(privacy, analytics);
    const ga = analytics?.properties?.GA4, fb = analytics?.properties?.FACEBOOK;
    const destinations = {
      ga4: flags.googleAnalytics?.enabled === true && ga?.status === true && ga.primary?.status === true &&
        /^G-[A-Z0-9]+$/.test(ga.primary?.id || '') ? ga.primary.id : '',
      meta: flags.metaPixel?.enabled === true && fb?.status === true && fb.primary?.status === true &&
        /^\d+$/.test(fb.primary?.id || '') ? fb.primary.id : '',
    };
    const result = {};
    for (const [channel, permitted] of [['ga4', gaAllowed], ['meta', metaAllowed]]) {
      if (name === 'saved_for_you_created' && channel === 'meta') result[channel] = 'not_applicable';
      else if (!permitted) result[channel] = 'consent_blocked';
      else if (!destinations[channel]) result[channel] = 'destination_unavailable';
      else if (!isProduction()) result[channel] = 'preview';
      else {
        try {
          result[channel] = (channel === 'ga4' ? google(destinations[channel], event) : meta(destinations[channel], event)) === true
            ? 'queued' : 'transport_unavailable';
        } catch { result[channel] = 'transport_unavailable'; }
      }
    }
    // Preview diagnostics are local/transient. Never replay interactions after consent changes.
    diagnose({ name, result, ...((name === 'saved_for_you_created' ? gaAllowed : gaAllowed || metaAllowed) ? { params: event.params } : {}) });
    return result;
  };
}

export function identity(data = {}) {
  return { productId: numericId(data.product?.id || data.productId || data.wishlistItem?.product?.id),
    variantId: numericId(data.variant?.id || data.variantId || data.wishlistItem?.variantId) };
}

export function guardLegacyWishlist(analytics) {
  const original = analytics?.findElemInPath;
  if (typeof original !== 'function' || original.scWishlistGuard) return;
  const wrapped = function (path, attributes, ...rest) {
    if (attributes && attributes === analytics.wishlist_btn_attributes && Array.isArray(path) && path.some(node =>
      node?.closest?.('wishlist-button-product, wishlist-button-collection, wishlist-button-block, wishlist-product-card, #sc-swish-dialog'))) return null;
    return original.call(this, path, attributes, ...rest);
  };
  wrapped.scWishlistGuard = true;
  analytics.findElemInPath = wrapped;
}

export function installNativeGa4Fallback(app, env, { google, production, diagnose = () => {} }) {
  const service = app?.apis?.tracking?.services?.find(s => s.eventNames?.get?.('addToWishlist') === 'add_to_wishlist');
  if (!service || typeof service.track !== 'function' || service.scGa4Fallback) return false;
  const original = service.track, seen = new Set();
  service.track = function (data) {
    const queue = env.dataLayer, start = Array.isArray(queue) ? queue.length : 0;
    const permittedAtStart = processingAllowed(env.Shopify?.customerPrivacy, env.analyzify).ga4;
    const result = original.call(this, data);
    try {
      if (data?.eventName === 'clearWishlist') seen.clear();
      if (data?.eventName === 'removeFromWishlist') {
        const productId = numericId(data.product?.id), variantId = numericId(data.variant?.id);
        for (const key of seen) if (key === `${productId}:${variantId}` || !variantId && key.startsWith(`${productId}:`)) seen.delete(key);
      }
      if (data?.eventName !== 'addToWishlist' || !Array.isArray(queue)) return result;
      const product = data.product, productId = numericId(product?.id);
      const candidates = data.wishlist?.items?.filter(item => numericId(item.product?.id) === productId) || [];
      const saved = candidates.find(item => numericId(item.variantId) === numericId(data.variant?.id) && numericId(item.variantId)) ||
        (candidates.length === 1 ? candidates[0] : null);
      const variant = data.variant || product?.variants?.find(v => numericId(v.id) === numericId(saved?.variantId)) ||
        (product?.variants?.length === 1 ? product.variants[0] : null);
      const variantId = numericId(variant?.id);
      if (!productId || !variantId) {
        diagnose({ name: 'add_to_wishlist', result: { ga4: 'native_identity_unavailable' },
          shape: { product: !!productId, variant: !!variantId } });
        return result;
      }
      // The SDK's synchronous object fallback proves its captured direct gtag was not used.
      const added = queue.slice(start), nativeItemId = String(data.variant?.sku || data.variant?.id || productId);
      const matchesItem = items => items?.length === 1 && items[0].affiliation === 'Wishlist King' &&
        String(items[0].item_id) === nativeItemId;
      const fallback = added.find(entry => entry?.event === 'add_to_wishlist' && matchesItem(entry.ecommerce?.items));
      const direct = added.some(entry => entry?.[0] === 'event' && entry[1] === 'add_to_wishlist' &&
        matchesItem(entry[2]?.items));
      const analytics = env.analyzify, ga = analytics?.properties?.GA4, id = ga?.primary?.id;
      if (!fallback || direct || analytics?.properties?.GTM?.status !== false) {
        diagnose({ name: 'add_to_wishlist', result: { ga4: !fallback ? 'native_object_unavailable' :
          direct ? 'native_direct' : 'native_gtm' } });
        return result;
      }
      const additionId = `${productId}:${variantId}`;
      if (seen.has(additionId)) return result;
      seen.add(additionId);
      const allowedNow = processingAllowed(env.Shopify?.customerPrivacy, analytics).ga4;
      let outcome = 'preview';
      if (!permittedAtStart || !allowedNow) outcome = 'consent_blocked';
      else if (app.settings?.integrations?.googleAnalytics?.enabled !== true || ga?.status !== true ||
        ga.primary?.status !== true || ga.primary?.events?.add_to_wishlist !== true ||
        !/^G-[A-Z0-9]+$/.test(id || '') || !analytics.gtag_config?.[id]) outcome = 'destination_unavailable';
      else if (production()) {
        const nativeItem = fallback.ecommerce.items[0], price = Number(variant.price) / 100;
        if (!nativeItem.item_id || !Number.isFinite(price) || price < 0 || !/^[A-Z]{3}$/.test(fallback.ecommerce.currency || '')) return result;
        const item = { item_id: String(nativeItem.item_id), item_name: String(product.title || '').slice(0, 200),
          item_variant: String(variant.title || '').slice(0, 200), item_variant_id: variantId,
          affiliation: 'Wishlist King', price, quantity: 1 };
        const params = { currency: fallback.ecommerce.currency, value: price, items: [item],
          product_id: productId, variant_id: variantId, ...(env.Shopify?.theme?.role === 'unpublished' ? { debug_mode: true } : {}) };
        outcome = google(id, { name: 'add_to_wishlist', params }) === true ? 'queued' : 'transport_unavailable';
      }
      diagnose({ name: 'add_to_wishlist', result: { ga4: outcome }, mode: env.Shopify?.theme?.role === 'unpublished' ? 'preview_qa' : 'normal',
        ...(permittedAtStart && allowedNow ? { params: { product_id: productId, variant_id: variantId } } : {}) });
    } catch { /* A vendor shape change leaves its original sender untouched. */ }
    return result;
  };
  service.scGa4Fallback = true;
  return true;
}

export function deliveryAllowed(config, env) {
  if (config?.dataset?.scWishlistTrackingMode !== 'auto' || env.Shopify?.designMode) return false;
  const url = new URL(env.location.href), theme = env.Shopify?.theme;
  if (theme?.role === 'main') return !url.searchParams.has('preview_theme_id');
  return theme?.role === 'unpublished' && String(theme.id) === config.dataset.scWishlistTrackingPreviewTheme &&
    url.searchParams.get('sc_wishlist_ga4_qa') === '1';
}

export function startWishlistAnalytics(config, env = window) {
  if (config.dataset.scTrackingBound) return;
  config.dataset.scTrackingBound = 'true';
  const doc = env.document;
  const flags = () => {
    try { return JSON.parse(doc.getElementById('wkAppSettings')?.textContent || '{}').settings?.integrations || {}; }
    catch { return {}; }
  };
  const production = () => deliveryAllowed(config, env);
  const google = (id, event) => {
    // Use the existing configured GA4 queue only; never initialize a tag/session or replay a missed interaction.
    if (!Array.isArray(env.dataLayer) || env.analyzify?.ga4Initiated !== true || !env.analyzify?.gtag_config?.[id]) return false;
    (function () { env.dataLayer.push(arguments); })('event', event.name, { ...event.params, send_to: id,
      ...(env.Shopify?.theme?.role === 'unpublished' ? { debug_mode: true } : {}) });
    return true;
  };
  const diagnose = detail => {
    if (detail.name === 'add_to_wishlist' && env.Shopify?.theme?.role === 'unpublished' && production()) {
      config.dataset.scNativeGa4Result = detail.result.ga4;
      config.dataset.scNativeGa4Shape = JSON.stringify(detail.shape || {});
    }
    doc.dispatchEvent(new CustomEvent('sc:wishlist-telemetry', { detail }));
  };
  const tracker = createTracker({
    getPrivacy: () => env.Shopify?.customerPrivacy,
    getAnalytics: () => env.analyzify,
    getFlags: () => ({ ...flags(), metaPixel: { enabled: false } }), isProduction: production,
    google,
    meta: (id, event) => {
      if (typeof env.fbq !== 'function') return false;
      env.fbq('trackSingleCustom', id, `SC_${event.name}`, event.params);
      return true;
    },
    diagnose,
  });
  const surface = node => node?.closest?.('#sc-swish-dialog') ? 'popup' : 'page';
  const currentSurface = () => doc.getElementById('sc-swish-dialog')?.open ? 'popup' : 'page';
  const pending = new Map(), nativeApps = new WeakSet(), seenEvents = new WeakSet();
  // Do not carry an in-flight measurement intent across a consent transition.
  doc.addEventListener('visitorConsentCollected', () => pending.clear());
  const key = item => `${item.productId}:${item.variantId}`;
  let timer, attempts = 0;
  const emit = (name, data) => tracker(name, data);
  const bindNative = () => {
    guardLegacyWishlist(env.analyzify);
    const app = env.WishlistKing;
    if (!app?.events?.subscribe) return false;
    env.SCWishlistNativePrivacy?.bind(app);
    installNativeGa4Fallback(app, env, { google, production, diagnose });
    if (nativeApps.has(app)) return true;
    nativeApps.add(app);
    const once = callback => event => {
      if (event && typeof event === 'object') {
        if (seenEvents.has(event)) return;
        seenEvents.add(event);
      }
      callback(event?.data || {});
    };
    app.events.subscribe('wk:wishlist:remove:success', once(data => {
      emit('wishlist_remove', { ...identity(data), source: 'manual', surface: currentSurface() });
    }));
    app.events.subscribe('wk:wishlist:clear:success', once(() => {
      emit('wishlist_clear', { source: 'manual', action: 'clear', surface: currentSurface() });
    }));
    app.events.subscribe('wk:wishlist:add:success', once(data => {
      const item = identity(data), intent = pending.get(key(item));
      if (!intent || Date.now() - intent.at > 15000) return;
      pending.delete(key(item));
      emit('saved_for_you_promote', { ...item, source: 'saved_for_you', surface: intent.surface, outcome: 'added' });
    }));
    // The fallback adapter wraps the native GA4 service; no second save or purchase subscription.
    return true;
  };
  const awaitNative = () => {
    clearTimeout(timer);
    if (!bindNative() && attempts++ < 120) timer = setTimeout(awaitNative, 500);
  };
  doc.addEventListener('wk:loader:wishlist-app:load', () => { attempts = 0; awaitNative(); });
  doc.addEventListener('sc:wishlist-action', event => {
    const data = event.detail;
    if (data?.name === 'saved_for_you_view' && data.surface === 'popup' && !doc.getElementById('sc-swish-dialog')?.open) return;
    emit(data?.name, data);
    if (data?.name === 'wishlist_open' && data.source === 'header') {
      const panel = doc.getElementById('sc-swish-dialog')?.querySelector('.sc-saved-panel:not([hidden])');
      if (panel) emit('saved_for_you_view', { source: 'saved_for_you', surface: 'popup', count: panel.querySelectorAll('.sc-saved-card').length });
    }
  });
  doc.addEventListener('sc:saved-existing-manual', event => {
    const intent = pending.get(event.detail);
    if (!intent || Date.now() - intent.at > 15000) return;
    pending.delete(event.detail);
    const [productId, variantId] = event.detail.split(':');
    emit('saved_for_you_promote', { productId, variantId, source: 'saved_for_you', surface: intent.surface, outcome: 'already_saved' });
  });
  doc.addEventListener('click', event => {
    if (event.button !== 0 || event.defaultPrevented) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    // Capture runs before Analyzify's generic bubbling click listener, even if it loaded late.
    guardLegacyWishlist(env.analyzify);
    bindNative();
    const promotion = target.closest('[data-sc-saved-start]')?.closest('[data-sc-saved-promote]');
    if (promotion) {
      const permitted = processingAllowed(env.Shopify?.customerPrivacy, env.analyzify);
      if (!permitted.ga4 && !permitted.meta) return;
      const item = identity(promotion.dataset);
      if (item.productId && item.variantId) {
        for (const [id, intent] of pending) if (Date.now() - intent.at > 15000) pending.delete(id);
        pending.set(key(item), { at: Date.now(), surface: surface(promotion) });
      }
      return;
    }
    const anchor = target.closest('a');
    if (!anchor) return;
    if (anchor.matches('.sc-wishlist-login-cta')) {
      emit('wishlist_login_click', { source: 'manual', surface: surface(anchor) }); return;
    }
    const manual = anchor.closest('wishlist-product-card');
    const saved = anchor.closest('.sc-saved-card');
    if (manual) emit('wishlist_item_click', { ...identity({ product: manual.wishlistItem?.product,
      variantId: manual.form?.selectedVariant?.id || manual.wishlistItem?.variantId }), source: 'manual', surface: surface(manual) });
    else if (saved) emit('wishlist_item_click', { ...identity(saved.dataset), source: 'saved_for_you', surface: surface(saved) });
  }, true);
  // A standalone manual wishlist view is distinct from repeatedly opening the modal.
  if (env.location.pathname === '/apps/wishlist') emit('wishlist_tab_view', { source: 'manual', surface: 'page' });
  awaitNative();
  env.addEventListener('pagehide', () => { clearTimeout(timer); pending.clear(); });
  return { emit, bindNative };
}

if (typeof document !== 'undefined') {
  const config = document.querySelector('[data-sc-wishlist-tracking]');
  if (config) startWishlistAnalytics(config);
}
